import {
  BookOpenText,
  CircleQuestionMark,
  ExternalLink,
  GraduationCap,
  MailQuestionMark,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import DropdownMenu from "./shared/DropdownMenu";
import DropdownMenuItem from "./shared/DropdownItem";
import { useNavigate } from "react-router-dom";
import { DiscordLogoIcon } from "@radix-ui/react-icons";
import { RAIL_ROW, RAIL_ROW_IDLE } from "./layout/navItems";

const helpCenterHighlightEvent = "insaion:highlightHelpCenter";

interface HelpDropdownMenuProps {
  /** Icon-only, for the collapsed rail. Otherwise a labelled row. */
  collapsed?: boolean;
  /** Lets the rail hold its hover peek open while the menu is showing. */
  onOpenChange?: (open: boolean) => void;
}

export default function HelpDropdownMenu({
  collapsed = false,
  onOpenChange,
}: HelpDropdownMenuProps = {}) {
  const navigate = useNavigate();
  const [isHighlighted, setIsHighlighted] = useState(false);
  const highlightTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const handleHighlight = () => {
      setIsHighlighted(true);
      if (highlightTimeoutRef.current) {
        window.clearTimeout(highlightTimeoutRef.current);
      }
      highlightTimeoutRef.current = window.setTimeout(() => {
        setIsHighlighted(false);
        highlightTimeoutRef.current = null;
      }, 2200);
    };

    window.addEventListener(helpCenterHighlightEvent, handleHighlight);

    return () => {
      window.removeEventListener(helpCenterHighlightEvent, handleHighlight);
      if (highlightTimeoutRef.current) {
        window.clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  return (
    <DropdownMenu
      className="w-full"
      onOpenChange={onOpenChange}
      trigger={
        <button
          type="button"
          title="Help & Support"
          className={`${RAIL_ROW} ${
            isHighlighted
              ? "bg-orange-500/10 text-orange-300 ring-2 ring-orange-400/70 animate-pulse"
              : RAIL_ROW_IDLE
          }`}
        >
          <MailQuestionMark className="h-5 w-5 shrink-0" aria-hidden="true" />
          <span className={collapsed ? "sr-only" : "truncate"}>
            Help &amp; Support
          </span>
        </button>
      }
    >
      <DropdownMenuItem
        onClick={() => {
          navigate("/contact-us");
        }}
      >
        <CircleQuestionMark size={16} className="mr-3 text-neutral-400" />
        <span>Contact Us</span>
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => {
          navigate("/learning-center");
        }}
      >
        <GraduationCap size={16} className="mr-3 text-neutral-400" />
        <span>Learning Center</span>
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => {
          window.open(
            "https://docs.insaion.com/",
            "_blank",
            "noopener,noreferrer",
          );
        }}
      >
        <BookOpenText size={16} className="mr-3 text-neutral-400" />
        <span>Documentation</span>
        <ExternalLink className="ml-auto h-4 w-4 text-neutral-400" />
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => {
          window.open(
            "https://discord.gg/ZJMPfMmu3S",
            "_blank",
            "noopener,noreferrer",
          );
        }}
      >
        <DiscordLogoIcon className="mr-3 text-neutral-400" />
        <span>Join Discord</span>
        <ExternalLink className="ml-auto h-4 w-4 text-neutral-400" />
      </DropdownMenuItem>
    </DropdownMenu>
  );
}
